// RenewableEnergy.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

function RenewableEnergy() {
  const plantsPerPage = 12; // <- desired per-page count
  const [plants, setPlants] = useState([]); // will hold current page (server-paged) OR full list (client-paged)
  const [allPlants, setAllPlants] = useState(null); // if API returns full list, we store here
  const [isServerPaged, setIsServerPaged] = useState(null); // true/false once we detect API behavior
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch helper that tries server-side paging first
  const fetchPlants = async (page = 1) => {
    setLoading(true);
    try {
      // Call backend with page & limit - backend may ignore them.
      const res = await axios.get(
        `https://backend-zkgn.onrender.com/api/renewableplants?page=${page}&limit=${plantsPerPage}`
      );

      // If backend responds with object having `plants` and `totalPages` -> server-side pagination
      if (res.data && (res.data.plants || res.data.totalPages || res.data.page)) {
        // Treat as server-paged response shape
        const pagePlants = res.data.plants || [];
        setPlants(pagePlants);
        setIsServerPaged(true);
        setTotalPages(res.data.totalPages ? Number(res.data.totalPages) : Math.max(1, Math.ceil((res.data.total || pagePlants.length) / plantsPerPage)));
        setCurrentPage(res.data.page ? Number(res.data.page) : page);
      } else if (Array.isArray(res.data)) {
        // Backend returned full array -> client-side paging
        setAllPlants(res.data);
        setIsServerPaged(false);
        const calcTotal = Math.max(1, Math.ceil(res.data.length / plantsPerPage));
        setTotalPages(calcTotal);

        // compute current page slice
        const indexOfLast = page * plantsPerPage;
        const indexOfFirst = indexOfLast - plantsPerPage;
        setPlants(res.data.slice(indexOfFirst, indexOfLast));
        setCurrentPage(page);
      } else {
        // Unknown response shape -> try to be safe
        console.warn("Unexpected /renewableplants response:", res.data);
        setPlants([]);
        setIsServerPaged(false);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Failed to fetch plants:", err);
      // fall back to empty
      setPlants([]);
      setAllPlants(null);
      setIsServerPaged(false);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // initial & page-change effect
  useEffect(() => {
    // If we've already detected server-side paging, call fetch with page param.
    // Otherwise: attempt server-side call first — fetchPlants internally handles both cases.
    fetchPlants(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Delete handler (refetch current page appropriately)
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this renewable energy plant?")) return;
    try {
      await axios.delete(`https://backend-zkgn.onrender.com/api/renewableplants/${id}`);
      toast.success("Plant deleted successfully.");

      // If server-paged: re-fetch current page (so backend's totalPages & page content are correct)
      if (isServerPaged) {
        fetchPlants(currentPage);
        return;
      }

      // If client-paged: update local list + recalc page
      if (allPlants) {
        const updatedAll = allPlants.filter((p) => p.renergy_id !== id);
        setAllPlants(updatedAll);

        // recalc total pages and ensure currentPage within bounds
        const newTotal = Math.max(1, Math.ceil(updatedAll.length / plantsPerPage));
        setTotalPages(newTotal);
        const safePage = Math.min(currentPage, newTotal);
        setCurrentPage(safePage);

        // update current page slice
        const indexOfLast = safePage * plantsPerPage;
        const indexOfFirst = indexOfLast - plantsPerPage;
        setPlants(updatedAll.slice(indexOfFirst, indexOfLast));
      } else {
        // fallback: just refetch
        fetchPlants(currentPage);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete the plant.");
    }
  };

  // Client-side pagination helpers (used both modes; in server mode they simply call setCurrentPage)
  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    if (isServerPaged === false && allPlants) {
      // client-side: compute slice locally and update
      const indexOfLast = page * plantsPerPage;
      const indexOfFirst = indexOfLast - plantsPerPage;
      setPlants(allPlants.slice(indexOfFirst, indexOfLast));
      setCurrentPage(page);
    } else {
      // server-side or unknown: request page from backend
      setCurrentPage(page);
    }
  };

  const goPrev = () => paginate(Math.max(1, currentPage - 1));
  const goNext = () => paginate(Math.min(totalPages, currentPage + 1));

  // Compact pagination (like: 1 ... 4 5 6 ... 20)
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  if (loading) return <div className="text-center mt-5">Loading...</div>;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h5 className="fw-bold text-tertiary mb-2">
          <strong>{isServerPaged ? (plants?.length || 0) : (allPlants ? allPlants.length : plants.length)}</strong> - Renewable Energy Plants Found
        </h5>
        <Link to="/addrenewableenergyplant" className="btn btn-primary">Add Renewable Energy Plant</Link>
      </div>

      <div className="row">
        {plants.map((plant) => (
          <div key={plant.renergy_id} className="col-lg-4 col-md-6 mb-4">
            <div className="card h-100 shadow-sm position-relative">
              <div className="card-body">
                <Link
                  className="plant-link"
                  to={`/renewableenergydashboard/${(plant.renergy_name || "").replace(/\s+/g, "-")}/${plant.renergy_id}`}
                >
                  <h5 className="card-title fw-bold text-dark">{plant.renergy_name}</h5>
                </Link>
                <p className="mb-1 plantdetails">
                  <strong style={{ color: "#D84D10" }}>Address:</strong> {plant.renergy_city}, {plant.renergy_state}
                </p>
                <p className="mb-0 plantdetails">
                  <strong style={{ color: "#D84D10" }}>Contact:</strong> {plant.renergy_phone}, {plant.renergy_email}
                </p>
              </div>

              <div className="position-absolute bottom-0 end-0 p-2 d-flex">
                <Link to={`/editrenewableenergyplant/${(plant.renergy_name || "").replace(/\s+/g, "-")}/${plant.renergy_id}`} className="btn btn-link text-success p-0 me-3">
                  <i className="fa fa-edit"></i>
                </Link>
                <button onClick={() => handleDelete(plant.renergy_id)} className="btn btn-link text-danger p-0">
                  <i className="fa fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compact Pagination */}
      {totalPages > 1 && (
        <nav className="d-flex justify-content-center mt-4">
          <ul className="pagination pagination-sm flex-wrap shadow-sm">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={goPrev}>← Previous</button>
            </li>

            {getPageNumbers().map((p, idx) => p === "..."
              ? <li key={idx} className="page-item disabled"><span className="page-link">...</span></li>
              : <li key={idx} className={`page-item ${currentPage === p ? "active" : ""}`}><button className="page-link" onClick={() => paginate(p)}>{p}</button></li>
            )}

            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={goNext}>Next →</button>
            </li>
          </ul>
        </nav>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
}

export default RenewableEnergy;
